import React, { useContext, useState, useMemo, ChangeEvent, KeyboardEvent, useEffect } from 'react'
import * as yup from 'yup'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'
import debounce from 'lodash.debounce'
import { GridItem } from '@zuto/core/src/grid'
import MuiCurrencyInput from '@zuto/molecules/src/Mui/CurrencyInput/MuiCurrencyInput'
import QuestionComponentProps from '../../../Questions/question-component-props'
import QuestionKey from '../../../Questions/question-key'
import { MonthlyIncomeValidator, monthlyIncomeValidationRules } from '../../../validators/MonthlyIncomeValidator'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import { handlePrevButtonClick, questionFontStyle, fireReactGAEvent } from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'

const StyledDiv = styled.div<SpaceProps>`
  ${space}
`
const validator = MonthlyIncomeValidator()
const { thresholds } = monthlyIncomeValidationRules
const validationSchema = validator.getValidator()

export const MonthlyIncomeQuestion = ({ existingAnswers, disablePrevious, updateFollowingPrefillStep }: QuestionComponentProps) => {
  const { setQuestionName, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const { featureSwitches, dispatch } = useContext(ApplicationFormContext)
  const { reviewPageV3, questionCopyChangeV1 } = featureSwitches ?? {}
  const [input, setInput] = useState<string>(existingAnswers ?? '')
  const [isDirty, setIsDirty] = useState(!!existingAnswers)
  // The error shows once the income has been wrong for the delay (at once for an existing answer). This records that
  // the delay has passed; whether the error is on screen is derived below.
  const [errorDelayElapsed, setErrorDelayElapsed] = useState(isDirty)
  const reviewPageV3AndClickedRequired = reviewPageV3 && hasNavigatedFromRequiredClick

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('monthlyIncome')
    }
  }, [])

  const errorMessage: string = useMemo(() => {
    try {
      validationSchema.validateSync({ monthlyIncome: input })
    } catch (untypedError: unknown) {
      const errorObject = untypedError as yup.ValidationError

      return errorObject.message
    }

    return ''
  }, [input])

  const incomeHasError = isDirty && !!errorMessage
  const showError = incomeHasError && errorDelayElapsed

  const debouncedSetShowError = useMemo(() => {
    const inputOverValidationThreshold = Number(input) > thresholds.valid.max
    const delay = inputOverValidationThreshold ? 0 : 1500
    return debounce(() => {
      setErrorDelayElapsed(true)
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Validation Error',
        label: 'Monthly Income',
        nonInteraction: true
      })
    }, delay)
  }, [input])

  // Start the delay while the income is wrong and the error is not yet on screen
  useEffect(() => {
    if (incomeHasError && !showError) debouncedSetShowError()
  }, [input, isDirty, showError, errorMessage])

  // While the income is right, the next error gets the full delay again
  const restartErrorDelayIfValid = () => {
    if (!incomeHasError) setErrorDelayElapsed(false)
  }

  const displayNextButton: boolean = useMemo(() => isDirty && !errorMessage, [isDirty, errorMessage])

  const canDisablePrevious = disablePrevious ? 'invisible' : undefined
  const canDisablePreviousForReviewFeature = reviewPageV3 ? !displayNextButton : canDisablePrevious

  const nextQuestion = () => {
    if (displayNextButton) {
      dispatch({
        type: 'QUESTION_ANSWERED',
        name: QuestionKey.MONTHLY_INCOME,
        value: input
      })
    }
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) updateFollowingPrefillStep(true, true, true)
      if (!hasNavigatedFromRequiredClick) updateFollowingPrefillStep(true, false, true)
    }
  }

  const previousQuestion = () => {
    handlePrevButtonClick(dispatch, {
      name: QuestionKey.MONTHLY_INCOME,
      value: input
    })
    if (updateFollowingPrefillStep) {
      if (reviewPageV3AndClickedRequired || !hasNavigatedFromRequiredClick) updateFollowingPrefillStep(false, false, true)
    }
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isDirty) setIsDirty(true)

    restartErrorDelayIfValid()
    setInput(event.currentTarget.value)
  }

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
    if (event.code === 'Enter' || event.key === 'Enter') nextQuestion()
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="174px"
      >
        <AppFormQuestion
          questionKey={QuestionKey.MONTHLY_INCOME}
          mt={3}
          mb={3}
          textStyle={questionFontStyle}
        >
          {questionCopyChangeV1 ? "What's your personal monthly income after tax?" : "What's your monthly income after tax?"}
        </AppFormQuestion>
        <StyledDiv mb={5}>
          <MuiCurrencyInput
            adornmentLabel="£"
            aria-label="label-currency-input"
            errorText={showError ? errorMessage : undefined}
            helperText={validator.validateHelperMessage(input)}
            label="Monthly income (after tax)"
            onKeyPress={onKeyPress}
            onChange={onChange}
            value={input}
            formatToGBNumber={true}
          />
        </StyledDiv>
      </GridItem>
      <NavigationButtons
        nextHandler={nextQuestion}
        previousHandler={previousQuestion}
        disableNext={!displayNextButton}
        disablePrevious={canDisablePreviousForReviewFeature}
      />
    </>
  )
}
